import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useAuthContext } from '../hooks/useAuthContext';
import { useCompanyContext } from '../hooks/useCompanyContext';

const DEFAULT_ROLES = [
  'Administratie',
  'Orders goedkeuren',
  'Teelt',
  'Transport',
  'Dossier Teelt'
];

// portalEnabled = true zodra er business-rollen zijn (anders false)
const hasBusinessRoles = (roles) =>
  Array.isArray(roles) && roles.filter(r => r && r.toLowerCase() !== 'gebruiker').length > 0;

export default function UserAccessModal({ open, onClose, user, onSaved }) {
  const { user: authUser } = useAuthContext();
  const { company } = useCompanyContext();

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // originele rollen en companies van server
  const originalRoles = useMemo(
    () => (Array.isArray(user?.roles) ? [...user.roles] : []),
    [user]
  );

  const originalCompanies = useMemo(
    () => (Array.isArray(user?.companies) ? [...user.companies] : []),
    [user]
  );

  const [roles, setRoles] = useState(originalRoles);
  const [companies, setCompanies] = useState(originalCompanies);
  const [portalEnabled, setPortalEnabled] = useState(hasBusinessRoles(originalRoles));

  const [availableCompanies, setAvailableCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [selectedCompanyToAdd, setSelectedCompanyToAdd] = useState("");

  // Gearchiveerde bedrijven mogen nergens in de modal worden getoond
  // of opnieuw aan een gebruiker worden gekoppeld.
  const activeCompanies = useMemo(
    () => availableCompanies.filter((c) => c?.archived !== true),
    [availableCompanies]
  );

  const archivedCompanyIds = useMemo(
    () => new Set(
      availableCompanies
        .filter((c) => c?.archived === true)
        .map((c) => c?.Companyid)
        .filter(Boolean)
    ),
    [availableCompanies]
  );

  // reset bij openen/wisselen
  useEffect(() => {
    const startRoles = Array.isArray(user?.roles) ? [...user.roles] : [];
    const startCompanies = Array.isArray(user?.companies) ? [...user.companies] : [];

    setRoles(startRoles);
    setCompanies(startCompanies);
    setPortalEnabled(hasBusinessRoles(startRoles));
    setSelectedCompanyToAdd("");
    setError(null);
  }, [user, open]);

  // laat portalEnabled automatisch volgen
  useEffect(() => {
    setPortalEnabled(hasBusinessRoles(roles));
  }, [roles]);

  // haal alle bedrijven op van dezelfde cultivation company
  useEffect(() => {
    const fetchCompanies = async () => {
      const cultivationCompanyId =
        user?.cultivation_company || company?.cultivation_company;

      if (!open || !cultivationCompanyId || !authUser?.token) return;

      try {
        setCompaniesLoading(true);

        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/companies/by-cultivation/${cultivationCompanyId}`,
          {
            headers: { Authorization: `Bearer ${authUser.token}` },
          }
        );

        setAvailableCompanies(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        console.error("❌ Bedrijven ophalen mislukt:", e?.response?.data || e.message);
      } finally {
        setCompaniesLoading(false);
      }
    };

    fetchCompanies();
  }, [open, user?.cultivation_company, company?.cultivation_company, authUser?.token]);

  const toggleRoleLocal = (r) => {
    setRoles((prev) => prev.includes(r) ? prev.filter(x => x !== r) : [...prev, r]);
  };

  const addCompanyLocal = () => {
    if (!selectedCompanyToAdd) return;

    setCompanies((prev) =>
      prev.includes(selectedCompanyToAdd) ? prev : [...prev, selectedCompanyToAdd]
    );
    setSelectedCompanyToAdd("");
  };

  const removeCompanyLocal = (companyId) => {
    setCompanies((prev) => prev.filter((c) => c !== companyId));
  };

  const companyNameMap = useMemo(() => {
    const map = {};
    for (const c of availableCompanies) {
      if (c?.Companyid) {
        map[c.Companyid] = c.Name || c.Companyid;
      }
    }
    return map;
  }, [availableCompanies]);

  const symDiff = (a, b) => {
    const sa = new Set(a); const sb = new Set(b);
    const onlyA = [...sa].filter(x => !sb.has(x));
    const onlyB = [...sb].filter(x => !sa.has(x));
    return [...onlyA, ...onlyB];
  };

  const areCompaniesEqual = (a, b) => {
    const aa = [...(a || [])].sort();
    const bb = [...(b || [])].sort();
    return JSON.stringify(aa) === JSON.stringify(bb);
  };

  const toToggle = symDiff(originalRoles, roles);
  const companiesChanged = !areCompaniesEqual(originalCompanies, companies);
  const isDirty = toToggle.length > 0 || companiesChanged;

  if (!open || !user) return null;

  const handleSave = async () => {
    if (!isDirty) {
      onClose?.();
      return;
    }

    setSaving(true);
    setError(null);

    try {
      let lastUpdatedRoles = [...originalRoles];

      for (const role of toToggle) {
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/user/update-role`,
          {
            userId: user._id,
            role,
            companyId: company?.Companyid
          },
          { headers: { Authorization: `Bearer ${authUser.token}` } }
        );
        lastUpdatedRoles = res.data?.updatedRoles || lastUpdatedRoles;
      }

      const finalRoles = toToggle.length === 0 ? roles : lastUpdatedRoles;
      const finalPortalEnabled = hasBusinessRoles(finalRoles);

      let finalCompanies = [...companies];

      if (companiesChanged) {
        const resCompanies = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/user/update-companies`,
          {
            userId: user._id,
            companies,
          },
          { headers: { Authorization: `Bearer ${authUser.token}` } }
        );

        finalCompanies = resCompanies.data?.updatedCompanies || finalCompanies;
      }

      onSaved?.({
        ...user,
        roles: finalRoles,
        companies: finalCompanies,
        portalEnabled: finalPortalEnabled,
      });

      onClose?.();
    } catch (e) {
      console.error(e);
      setError('Opslaan mislukt. Controleer je rechten of probeer opnieuw.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={backdrop} onClick={onClose}>
      <div style={modal} onClick={(e) => e.stopPropagation()}>
        <div style={header}>
          <h3 style={{ margin: 0 }}>Gebruikersrechten</h3>
          <button onClick={onClose} style={iconBtn} aria-label="Sluiten">✕</button>
        </div>

        <div style={body}>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontWeight: 600 }}>{user.name || user.Naam || user.email}</div>
          </div>

          <label style={{ display: 'flex', alignItems: 'center' }}>
            <input type="checkbox" checked={portalEnabled} readOnly />
            <span style={{ marginLeft: 8 }}>Toegang tot klantportaal</span>
          </label>

          <div style={{ marginTop: 16 }}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Rollen</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 8 }}>
              {DEFAULT_ROLES.map((r) => (
                <label key={r} style={{ display: 'flex', alignItems: 'center' }}>
                  <input
                    type="checkbox"
                    checked={roles.includes(r)}
                    onChange={() => toggleRoleLocal(r)}
                    disabled={saving}
                  />
                  <span style={{ marginLeft: 8 }}>{r}</span>
                </label>
              ))}
            </div>
          </div>

          <div style={{ marginTop: 16 }}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Toegang tot bedrijven</div>

            {companiesLoading ? (
              <div style={{ fontSize: 14, color: "#666", marginBottom: 12 }}>
                Bedrijven laden…
              </div>
            ) : (
              <>
                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
                  {[...companies]
                    .filter((companyId) => !archivedCompanyIds.has(companyId))
                    .sort((a, b) => {
                      const nameA = `${companyNameMap[a] || ""} ${a}`.toLowerCase();
                      const nameB = `${companyNameMap[b] || ""} ${b}`.toLowerCase();
                      return nameA.localeCompare(nameB);
                    })
                    .map((companyId) => (
                      <div
                        key={companyId}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 12,
                          padding: "8px 10px",
                          background: "#f1f3f5",
                          borderRadius: 10,
                        }}
                      >
                        <span style={{ fontSize: 14 }}>
                          <strong>{companyId}</strong> — {companyNameMap[companyId] || "Onbekend"}
                        </span>

                        <button
                          type="button"
                          onClick={() => removeCompanyLocal(companyId)}
                          disabled={saving}
                          style={{
                            background: "#fee2e2",
                            color: "#991b1b",
                            border: "none",
                            borderRadius: 6,
                            padding: "6px 10px",
                            cursor: "pointer",
                          }}
                        >
                          Verwijderen
                        </button>
                      </div>
                    ))}
                </div>

                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <select
                    value={selectedCompanyToAdd}
                    onChange={(e) => setSelectedCompanyToAdd(e.target.value)}
                    disabled={saving}
                    style={{
                      flex: 1,
                      padding: "8px 10px",
                      borderRadius: 8,
                      border: "1px solid #ddd",
                    }}
                  >
                    <option value="">Kies bedrijf…</option>
                    {activeCompanies
                      .filter((c) => !companies.includes(c.Companyid))
                      .sort((a, b) =>
                        `${a.Name || ""} ${a.Companyid}`.localeCompare(`${b.Name || ""} ${b.Companyid}`)
                      )
                      .map((c) => (
                        <option key={c.Companyid} value={c.Companyid}>
                          {c.Companyid} — {c.Name}
                        </option>
                      ))}
                  </select>

                  <button
                    type="button"
                    onClick={addCompanyLocal}
                    disabled={!selectedCompanyToAdd || saving}
                    style={{
                      background: "#0d6efd",
                      color: "#fff",
                      border: "none",
                      borderRadius: 8,
                      padding: "8px 12px",
                      cursor: "pointer",
                    }}
                  >
                    Toevoegen
                  </button>
                </div>
              </>
            )}
          </div>

          {error && <div style={{ color: '#b00020', marginTop: 12 }}>{error}</div>}
        </div>

        <div style={footer}>
          <button onClick={onClose} style={secondaryBtn} disabled={saving}>Annuleren</button>
          <button onClick={handleSave} style={primaryBtn} disabled={saving || !isDirty}>
            {saving ? 'Opslaan…' : 'Opslaan'}
          </button>
        </div>
      </div>
    </div>
  );
}

const backdrop = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.35)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
  boxSizing: 'border-box',
  zIndex: 9999
};

const modal = {
  background: '#fff',
  borderRadius: 12,
  width: '100%',
  maxWidth: 620,
  maxHeight: 'calc(100vh - 32px)',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
  overflow: 'hidden'
};

const header = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '14px 16px',
  borderBottom: '1px solid #eee',
  flexShrink: 0
};

const body = {
  padding: 16,
  overflowY: 'auto',
  minHeight: 0
};

const footer = {
  padding: 16,
  display: 'flex',
  gap: 8,
  justifyContent: 'flex-end',
  borderTop: '1px solid #eee',
  flexShrink: 0
};

const iconBtn = {
  background: 'transparent',
  border: 'none',
  fontSize: 18,
  cursor: 'pointer'
};

const primaryBtn = {
  background: '#0d6efd',
  color: '#fff',
  border: 'none',
  padding: '10px 14px',
  borderRadius: 8,
  cursor: 'pointer'
};

const secondaryBtn = {
  background: '#f1f3f5',
  color: '#222',
  border: 'none',
  padding: '10px 14px',
  borderRadius: 8,
  cursor: 'pointer'
};